import { Component, OnInit, OnChanges, SimpleChanges, OnDestroy, Output, EventEmitter, Input } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';
import { MonitorPolicy, MonitorSettings_Config } from 'DiscoveryEngine.TS/Monitors';
import { Subscription } from 'rxjs';
import { ConfigMonitorComponentState } from '../..';
import { MonitorConfigComponentBase } from '../MonitorConfigComponentBase';


@Component({
	selector: 'config-settings',
	templateUrl: './configsettings.component.html',
	styleUrls: ['./configsettings.component.scss']
})
export class ConfigSettingsComponent extends MonitorConfigComponentBase implements OnInit, OnChanges, OnDestroy {

	@Input()
	public monEditorState: ConfigMonitorComponentState;

	public name: UntypedFormControl;
	public enabled: UntypedFormControl;
	public captureStartup: UntypedFormControl;
	public captureRunning: UntypedFormControl;
	public captureSyslog: UntypedFormControl;
	public captureCustom: UntypedFormControl;
	public customScript: UntypedFormControl;
	public configExportFolder: UntypedFormControl;
	public createDateSubfolder: UntypedFormControl;
	public createDeviceSubfolder: UntypedFormControl;
	public saveChangesOnly: UntypedFormControl;
	public exportToServer: UntypedFormControl;
	public keep: UntypedFormControl;
	public keepCommandPrompt: UntypedFormControl;

	private copyChangesSub: Subscription;

	constructor(private fb: UntypedFormBuilder) {
		super();

		this.copyChangesSub = null;
	}

	public ngOnInit(): void {
		this.BuildForm();
	}

	public ngOnChanges(changes: SimpleChanges): void {

		this.unsubscribeAllEvents();

		if (this.monEditorState) {
			this.copyChangesSub = this.monEditorState.copyChanges.subscribe(
				(result: boolean) => {
					if (result) {
						this.copyChanges();
					}
				}
			);
		}

		this.BuildForm();
	}

	public ngOnDestroy(): void {
		this.unsubscribeAllEvents();
	}

	private unsubscribeAllEvents(): void {
		if (this.copyChangesSub) {
			this.copyChangesSub.unsubscribe();
			this.copyChangesSub = null;
		}
	}

	public BuildForm(): void {
		this.customScript = undefined;

		let name = "";
		let enabled = false;
		let captureStartup = false;
		let captureRunning = false;
		let captureSyslog = false;
		let captureCustom = false;
		let customScript = "";
		let configExportFolder = "";
		let createDateSubfolder = false;
		let createDeviceSubfolder = false;
		let saveChangesOnly = false;
		let exportToServer = false;
		let keep = 1;
		let keepCommandPrompt = false;

		if (!!this.editor && this.editor.HasPolicy) {
			let monPolicy: MonitorPolicy = this.editor.Policy;
			let monSettings: MonitorSettings_Config = <MonitorSettings_Config>monPolicy.Settings;

			name = monPolicy.Name;
			enabled = monPolicy.Enabled;
			captureStartup = monSettings.GetStartupConfig;
			captureRunning = monSettings.GetRunningConfig;
			captureSyslog = monSettings.GetSyslog;
			captureCustom = monSettings.RunCustomScript;
			customScript = monSettings.CustomScript.replace("\r\n\r\n", "\r\n");
			configExportFolder = (monSettings.HasConfigExportFolder ? monSettings.ConfigExportFolder : "");
			createDateSubfolder = monSettings.CreateDateSubfolder;
			createDeviceSubfolder = monSettings.CreateDeviceSubfolder;
			saveChangesOnly = monSettings.ConfigSaveChangesOnly;
			exportToServer = monSettings.ExportConfigsToServer;
			keep = monSettings.Keep;
			keepCommandPrompt = monSettings.KeepCommandPrompt;
		}

		this.name = new UntypedFormControl(name, Validators.required);
		this.enabled = new UntypedFormControl(enabled);
		this.captureStartup = new UntypedFormControl(captureStartup);
		this.captureRunning = new UntypedFormControl(captureRunning);
		this.captureSyslog = new UntypedFormControl(captureSyslog);
		this.captureCustom = new UntypedFormControl(captureCustom);
		this.customScript = new UntypedFormControl(customScript);
		this.configExportFolder = new UntypedFormControl(configExportFolder);
		this.createDateSubfolder = new UntypedFormControl(createDateSubfolder);
		this.createDeviceSubfolder = new UntypedFormControl(createDeviceSubfolder);
		this.saveChangesOnly = new UntypedFormControl(saveChangesOnly);
		this.exportToServer = new UntypedFormControl(exportToServer);
		this.keep = new UntypedFormControl(keep, Validators.compose([Validators.required, this.minValue(1), this.maxValue(99)]));
		this.keepCommandPrompt = new UntypedFormControl(keepCommandPrompt);

		// Notify devices tab of changes that affect the device tab's display
		this.captureStartup.valueChanges.subscribe(() => this.settingsChange());
		this.captureRunning.valueChanges.subscribe(() => this.settingsChange());
		this.captureSyslog.valueChanges.subscribe(() => this.settingsChange());
		this.captureCustom.valueChanges.subscribe(() => this.settingsChange());

		this.SettingsForm = this.fb.group({
			name: this.name,
			enabled: this.enabled,
			captureStartup: this.captureStartup,
			captureRunning: this.captureRunning,
			captureSyslog: this.captureSyslog,
			captureCustom: this.captureCustom,
			customScript: this.customScript,
			configExportFolder: this.configExportFolder,
			createDateSubfolder: this.createDateSubfolder,
			createDeviceSubfolder: this.createDeviceSubfolder,
			saveChangesOnly: this.saveChangesOnly,
			exportToServer: this.exportToServer,
			keep: this.keep,
			keepCommandPrompt: this.keepCommandPrompt,
		},
			{
				validator: Validators.compose([
					this.atLeastOneConfigSelected(this.captureStartup, this.captureRunning, this.captureCustom, this.captureSyslog)
				])
			});
	}

	private atLeastOneConfigSelected(a: UntypedFormControl, b: UntypedFormControl, c: UntypedFormControl, d: UntypedFormControl): ValidatorFn {
		return () => {
			if (a.value || b.value || c.value || d.value) {
				return null;
			}
			else {
				let result = {};
				result["atLeastOneConfigSelected"] = { valid: false };
				return result;
			}
		};
	}

	private settingsChange(): void {
		this.copyChanges();
		this.monEditorState.settingsChange.emit(true);
	}

	public applyTempChanges(): void {
		if (!!this.editor && this.editor.HasPolicy) {
			this.copyChanges();
			this.BuildForm();
		}
	}

	private copyChanges(): void {
		let monPolicy: MonitorPolicy = this.editor.Policy;
		let monSettings: MonitorSettings_Config = <MonitorSettings_Config>monPolicy.Settings;

		monPolicy.Name = this.name.value;
		monPolicy.Enabled = this.enabled.value;
		monSettings.GetStartupConfig = this.captureStartup.value;
		monSettings.GetRunningConfig = this.captureRunning.value;
		monSettings.GetSyslog = this.captureSyslog.value;
		monSettings.RunCustomScript = this.captureCustom.value;

		let customScript = this.customScript.value;
		customScript = customScript.replace("\r\n\r\n", "\r\n");
		monSettings.CustomScript = customScript;
		monSettings.ConfigExportFolder = this.configExportFolder.value;
		monSettings.CreateDateSubfolder = this.createDateSubfolder.value;
		monSettings.CreateDeviceSubfolder = this.createDeviceSubfolder.value;
		monSettings.ConfigSaveChangesOnly = this.saveChangesOnly.value;
		monSettings.ExportConfigsToServer = this.exportToServer.value;
		monSettings.Keep = this.keep.value;
		monSettings.KeepCommandPrompt = this.keepCommandPrompt.value;
	}

	public get invalidTempChanges(): string[] {
		return ["Settings"];
	}
}
